import React, { Fragment, useState } from "react";

import { Dialog, Transition } from "@headlessui/react";

const MESSAGE_MAX_LENGTH = 5000;

function formatDateTime(iso) {
  if (!iso) return "-";
  const d = new Date(iso);

  if (Number.isNaN(d.getTime())) return "-";

  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "numeric",
    minute: "2-digit",
  });
}

/**
 * Notes tab for the customer detail page. Renders every admin note with its
 * author and timestamps, plus create/edit/delete flows. Data and API calls
 * live in the parent; the handlers passed in resolve on success and reject
 * on failure (dialogs stay open on failure so nothing is lost).
 */
const CustomerNotesTab = ({
  loading,
  notes,
  onCreateNote,
  onDeleteNote,
  onUpdateNote,
}) => {
  // editorTarget: null = closed, {} = create, { note } = edit
  const [editorTarget, setEditorTarget] = useState(null);
  const [editorMessage, setEditorMessage] = useState("");
  const [editorInFlight, setEditorInFlight] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteInFlight, setDeleteInFlight] = useState(false);

  const openCreate = () => {
    setEditorMessage("");
    setEditorTarget({});
  };

  const openEdit = (note) => {
    setEditorMessage(note.message);
    setEditorTarget({ note });
  };

  const closeEditor = () => {
    if (editorInFlight) return;
    setEditorTarget(null);
  };

  const handleEditorSave = async () => {
    const message = editorMessage.trim();

    if (!message) return;
    setEditorInFlight(true);
    try {
      if (editorTarget?.note) {
        await onUpdateNote(editorTarget.note.id, message);
      } else {
        await onCreateNote(message);
      }
      setEditorTarget(null);
    } catch (error) {
      // Parent already surfaced the error; keep the dialog open.
    } finally {
      setEditorInFlight(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteInFlight(true);
    try {
      await onDeleteNote(deleteTarget.id);
      setDeleteTarget(null);
    } catch (error) {
      // Parent already surfaced the error; keep the dialog open.
    } finally {
      setDeleteInFlight(false);
    }
  };

  const isEditing = !!editorTarget?.note;

  return (
    <div className="card shadow-md">
      <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold text-primary-600">Notes</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Internal admin notes - never visible to the customer.
          </p>
        </div>
        <button
          className="btn btn-primary btn-sm flex items-center gap-2"
          type="button"
          onClick={openCreate}
        >
          <i className="fas fa-plus" />
          Add Note
        </button>
      </div>

      {loading ? (
        <div className="p-6 text-center">
          <i className="fas fa-spinner fa-spin text-2xl text-primary-600" />
          <p className="mt-2 text-gray-500">Loading notes...</p>
        </div>
      ) : notes.length === 0 ? (
        <div className="p-6 text-center">
          <i className="fas fa-sticky-note text-4xl text-gray-300 mb-3" />
          <p className="text-gray-500">No notes for this customer yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="border-b border-gray-200 dark:border-gray-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase">
                  Note
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase whitespace-nowrap">
                  Created By
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase whitespace-nowrap">
                  Created
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 dark:text-gray-400 uppercase whitespace-nowrap">
                  Updated
                </th>
                <th className="px-4 py-3 w-20" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {notes.map((note, index) => (
                <tr
                  className={
                    index % 2 === 0 ? "" : "bg-gray-50/50 dark:bg-gray-800/30"
                  }
                  key={note.id}
                >
                  <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-100 max-w-md">
                    <p className="whitespace-pre-wrap break-words">
                      {note.message}
                    </p>
                  </td>
                  <td className="px-4 py-3 text-sm whitespace-nowrap">
                    <span className="inline-flex items-center gap-2">
                      <span className="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                        <i className="fas fa-user-shield text-[10px] text-primary-600 dark:text-primary-400" />
                      </span>
                      {note.author?.displayName || "Unknown admin"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {formatDateTime(note.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {note.isEdited ? formatDateTime(note.updatedAt) : "-"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-right">
                    <button
                      className="text-gray-400 hover:text-primary-600 transition-colors p-1"
                      title="Edit note"
                      type="button"
                      onClick={() => openEdit(note)}
                    >
                      <i className="fas fa-pen" />
                    </button>
                    <button
                      className="text-gray-400 hover:text-red-500 transition-colors p-1 ml-1"
                      title="Delete note"
                      type="button"
                      onClick={() => setDeleteTarget(note)}
                    >
                      <i className="fas fa-trash" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create / edit note dialog */}
      <Transition appear as={Fragment} show={!!editorTarget}>
        <Dialog as="div" className="relative z-50" onClose={closeEditor}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-lg transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                  <div className="px-7 pt-7 pb-5">
                    <div className="w-12 h-12 rounded-2xl bg-primary-50 dark:bg-primary-500/10 flex items-center justify-center mb-4 ring-1 ring-primary-500/20">
                      <i
                        className={`fas ${isEditing ? "fa-pen" : "fa-sticky-note"} text-primary-500 text-base`}
                      />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                      {isEditing ? "Edit note" : "Add a note"}
                    </Dialog.Title>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">
                      Visible to Lunova admins only.
                    </p>
                    <textarea
                      autoFocus
                      className="input w-full h-32 resize-none mt-4"
                      maxLength={MESSAGE_MAX_LENGTH}
                      placeholder="Enter the note..."
                      value={editorMessage}
                      onChange={(e) => setEditorMessage(e.target.value)}
                    />
                    <p className="mt-1 text-right text-xs text-gray-400 dark:text-gray-500 tabular-nums">
                      {editorMessage.length}/{MESSAGE_MAX_LENGTH}
                    </p>
                  </div>
                  <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex justify-end gap-2">
                    <button
                      className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                      disabled={editorInFlight}
                      type="button"
                      onClick={closeEditor}
                    >
                      Cancel
                    </button>
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-primary-500 to-primary-600 text-white hover:shadow-lg hover:shadow-primary-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 transition-all"
                      disabled={editorInFlight || !editorMessage.trim()}
                      type="button"
                      onClick={handleEditorSave}
                    >
                      {editorInFlight ? (
                        <>
                          <i className="fas fa-circle-notch fa-spin text-xs" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-check text-xs" />
                          {isEditing ? "Save Changes" : "Add Note"}
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Delete confirmation */}
      <Transition appear as={Fragment} show={!!deleteTarget}>
        <Dialog
          as="div"
          className="relative z-50"
          onClose={() => !deleteInFlight && setDeleteTarget(null)}
        >
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black/60" />
          </Transition.Child>
          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-2xl bg-white dark:bg-gray-800 shadow-2xl ring-1 ring-gray-200 dark:ring-white/5 transition-all">
                  <div className="px-7 pt-7 pb-5">
                    <div className="w-12 h-12 rounded-2xl bg-red-50 dark:bg-red-500/10 flex items-center justify-center mb-4 ring-1 ring-red-500/20">
                      <i className="fas fa-trash text-red-500 text-base" />
                    </div>
                    <Dialog.Title className="text-lg font-semibold text-gray-900 dark:text-white tracking-tight">
                      Delete this note?
                    </Dialog.Title>
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                      The note will be removed immediately. This cannot be
                      undone.
                    </p>
                  </div>
                  <div className="px-7 py-4 border-t border-gray-100 dark:border-gray-700/60 bg-gray-50/50 dark:bg-gray-900/30 rounded-b-2xl flex justify-end gap-2">
                    <button
                      className="px-5 py-2.5 text-sm font-medium rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 transition-colors"
                      disabled={deleteInFlight}
                      type="button"
                      onClick={() => setDeleteTarget(null)}
                    >
                      Cancel
                    </button>
                    <button
                      className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-xl bg-gradient-to-r from-red-500 to-red-600 text-white hover:shadow-lg hover:shadow-red-500/30 hover:-translate-y-0.5 disabled:opacity-50 disabled:shadow-none disabled:translate-y-0 transition-all"
                      disabled={deleteInFlight}
                      type="button"
                      onClick={handleConfirmDelete}
                    >
                      {deleteInFlight ? (
                        <>
                          <i className="fas fa-circle-notch fa-spin text-xs" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-trash text-xs" />
                          Delete
                        </>
                      )}
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

export default CustomerNotesTab;
